import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { router } from '@/router';
import ErrorBoundary from '@/components/ErrorBoundary';
import {
  bootstrapEdcUrlTokenSync,
  registerQueryCacheResetter,
} from '@/auth/edcTokenBridge';
import './index.css';

// ─── EDC → CTMS handoff, before anything else ───────────────────────────────
// Wave EDC's app launcher opens CTMS with `?token=<EDC JWT>`. This runs
// synchronously, ahead of createRoot().render(), so the credential is out of
// the address bar before the first paint and AuthProvider's init sees it
// ahead of the persisted-session path. A stale session belonging to a
// different user is purged here, not after components have already mounted
// (and fired queries) under the previous identity.
bootstrapEdcUrlTokenSync();

// ─── React Query client ─────────────────────────────────────────────────────

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

// Let the auth layer drop every cached query on logout or a user switch, so
// one user's data can never be served from cache to the next.
registerQueryCacheResetter(() => {
  void queryClient.cancelQueries();
  queryClient.clear();
});

// ─── Inner app: reads auth state and passes it to the router context ────────

function InnerApp() {
  const { isAuthenticated, isLoading } = useAuth();

  // When auth state changes, re-run route `beforeLoad` guards with the fresh
  // context. Without this the router keeps the stale `isAuthenticated` value
  // and a just-logged-in user gets bounced back to /login.
  React.useEffect(() => {
    router.invalidate();
  }, [isAuthenticated]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <RouterProvider
      router={router}
      context={{ isAuthenticated }}
    />
  );
}

// ─── Mount ──────────────────────────────────────────────────────────────────

const rootElement = document.getElementById('root')!;

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <InnerApp />
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);
